import { supabase } from "@/lib/supabase/client";
import { AssumptionDefinition, OutputDefinition } from "@/types/excel/types";

export interface ExcelTemplate {
  id: string;
  name: string;
  description: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  file_data?: any;
  mapping: AssumptionDefinition[];
  outputs: OutputDefinition[];
  version: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SaveTemplateParams {
  name: string;
  description?: string;
  file: File;
  mapping: AssumptionDefinition[];
  outputs: OutputDefinition[];
}

export const templateService = {
  async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  },

  async uploadToStorage(file: File, fileName: string): Promise<string> {
    const timestamp = Date.now();
    const uniqueFileName = `templates/${timestamp}_${fileName}`;

    const { data, error } = await supabase.storage
      .from("excel-templates")
      .upload(uniqueFileName, file, {
        cacheControl: "3600",
        upsert: true,
      });

    if (error) {
      console.error("Storage upload error:", error);
      throw error;
    }

    const { data: urlData } = supabase.storage
      .from("excel-templates")
      .getPublicUrl(data.path);

    return urlData.publicUrl;
  },

  async saveTemplate({
    name,
    description,
    file,
    mapping,
    outputs,
  }: SaveTemplateParams) {
    try {
      const base64 = await this.fileToBase64(file);

      const {
        data: { user },
      } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from("excel_templates")
        .insert({
          name,
          description: description || "",
          file_name: file.name,
          file_size: file.size,
          mime_type: file.type,
          file_data: base64,
          mapping,
          outputs,
          version: 1,
          is_active: false,
          created_by: user?.id || null,
        })
        .select()
        .single();

      if (error) {
        console.error("❌ Supabase error:", error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error("❌ Error saving template:", error);
      throw error;
    }
  },

  async getAllTemplates(): Promise<ExcelTemplate[]> {
    const { data, error } = await supabase
      .from("excel_templates")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching templates:", error);
      throw error;
    }

    return data || [];
  },

  async getActiveTemplate(): Promise<ExcelTemplate | null> {
    const { data, error } = await supabase
      .from("excel_templates")
      .select("*")
      .eq("is_active", true)
      .order("version", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error("Error fetching active template:", error);
      return null;
    }

    if (!data) {
      console.log("No active template found");
      return null;
    }

    return data;
  },

  async setTemplateActive(id: string, isActive: boolean): Promise<void> {
    if (isActive) {
      console.log("Deactivating all other templates...");

      const { error: deactivateError } = await supabase
        .from("excel_templates")
        .update({ is_active: false })
        .neq("id", id);

      if (deactivateError) {
        console.error("Error deactivating templates:", deactivateError);
        throw deactivateError;
      }
    }

    const { error } = await supabase
      .from("excel_templates")
      .update({
        is_active: isActive,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id);

    if (error) {
      console.error("Error setting template active status:", error);
      throw error;
    }
  },

  async getTemplateFile(
    template: ExcelTemplate,
  ): Promise<{ data: ArrayBuffer; fileName: string }> {
    if (!template.file_data) {
      throw new Error("No file data available for template");
    }

    const base64Data = template.file_data.includes("base64,")
      ? template.file_data.split("base64,")[1]
      : template.file_data;

    const binaryString = atob(base64Data);
    const buffer = new ArrayBuffer(binaryString.length);
    const view = new Uint8Array(buffer);

    for (let i = 0; i < binaryString.length; i++) {
      view[i] = binaryString.charCodeAt(i);
    }

    return {
      data: buffer,
      fileName: template.file_name,
    };
  },

  async downloadTemplateForPL(
    template: ExcelTemplate,
  ): Promise<{ data: ArrayBuffer; fileName: string }> {
    return this.getTemplateFile(template);
  },

  async downloadFromStorage(fileUrl: string): Promise<ArrayBuffer> {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error(
        `Failed to download from storage: ${response.statusText}`,
      );
    }

    const buffer = await response.arrayBuffer();

    return buffer;
  },
  async deleteTemplate(id: string): Promise<void> {
    const { error } = await supabase
      .from("excel_templates")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Error deleting template:", error);
      throw error;
    }
  },
};
